import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { OtpInput } from './OtpInput';
import { OtpVerifyButton } from './OtpVerifyButton';
import { MfaInputProps } from './type';

interface Props extends MfaInputProps {}

export const EmailOtpInput = ({
  expiresAt,
  forceResendLabel,
  loading,
  verified,
  disabled,
  length = 6,
  autoFocus,
  onSubmit,
  onSend,
  label,
  description,
}: Props) => {
  const t = useTranslations();
  const { user } = useAuth();
  const email = user?.email ?? t('Mfa_verify.r250917.Your_email');

  const { valueAtom, errorMessageAtom, onChange } = useBunja(emailOtpBunja);
  const value = useAtomValue(valueAtom);

  const errorMessage = useAtomValue(errorMessageAtom);

  const handleChange = (code: string) => {
    onChange(code);
    if (code.length === length) onSubmit?.('email', code);
  };

  return (
    <OtpInput
      channel='email'
      value={value}
      errorMessage={errorMessage}
      length={length}
      label={label ?? t('Mfa_verify.r250917.Check_your_email_title')}
      description={
        description ??
        `${t('Mfa_verify.r250917.Code_instruction_digit', { length, target: email })} ${t('Mfa_verify.r250917.Code_valid_duration', { duration: 10 })}`
      }
      disabled={verified || loading || disabled}
      autoFocus={autoFocus}
      action={
        <OtpVerifyButton
          onClick={onSend}
          loading={loading}
          verified={verified}
          expiresAt={expiresAt}
          forceResendLabel={forceResendLabel}
        />
      }
      onChange={handleChange}
    />
  );
};

export const emailOtpBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const valueAtom = atom('');
  const errorMessageAtom = atom('');

  function onChange(value: string): void {
    store.set(valueAtom, value);
    store.set(errorMessageAtom, '');
  }

  function setError(message: string): void {
    store.set(errorMessageAtom, message);
  }

  function clear(): void {
    store.set(valueAtom, '');
    store.set(errorMessageAtom, '');
  }

  return {
    valueAtom,
    errorMessageAtom,
    onChange,
    setError,
    clear,
  };
});

emailOtpBunja.debugLabel = 'emailOtpBunja';
